import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Brain, RefreshCw, CheckCircle, AlertTriangle, XCircle, 
  TrendingUp, TrendingDown, Minus, BarChart3, Download,
  ChevronLeft, ChevronRight, Image as ImageIcon, Filter
} from 'lucide-react';
import { toast } from 'sonner';

// Color options for correction dropdown
const COLOR_OPTIONS = [
  'Beige', 'Black', 'Blue', 'Brown', 'Green', 
  'Grey', 'Pink', 'Purple', 'Red', 'White', 'Yellow'
];

const AILearningDashboard = () => {
  const [activeView, setActiveView] = useState('overview'); // overview, review
  const [stats, setStats] = useState(null);
  const [colorAccuracy, setColorAccuracy] = useState(null);
  const [commonErrors, setCommonErrors] = useState(null);
  const [trend, setTrend] = useState(null);
  const [extractions, setExtractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState({});
  
  // Filters
  const [filterColor, setFilterColor] = useState('all');
  const [filterReviewed, setFilterReviewed] = useState('all');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, colorRes, errorsRes, trendRes] = await Promise.all([
        axios.get(`${API}/ai-learning/performance-stats`, { withCredentials: true }),
        axios.get(`${API}/ai-learning/accuracy-by-color`, { withCredentials: true }),
        axios.get(`${API}/ai-learning/common-errors`, { withCredentials: true }),
        axios.get(`${API}/ai-learning/accuracy-trend?days=30`, { withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setColorAccuracy(colorRes.data);
      setCommonErrors(errorsRes.data);
      setTrend(trendRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load AI performance data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch extractions for review
  const fetchExtractions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset);
      if (filterColor !== 'all') params.append('color', filterColor);
      if (filterReviewed !== 'all') params.append('reviewed', filterReviewed === 'reviewed');
      
      const res = await axios.get(`${API}/ai-learning/pending-reviews?${params}`, { 
        withCredentials: true 
      });
      
      setExtractions(res.data.extractions);
      setTotal(res.data.total);
    } catch (error) {
      console.error('Failed to fetch extractions:', error);
      toast.error('Failed to load images for review');
    }
  }, [offset, filterColor, filterReviewed]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (activeView === 'review') {
      fetchExtractions();
    }
  }, [activeView, fetchExtractions]);

  // Submit review
  const submitReview = async (extractionId, score, correctedColor = null) => {
    setReviewLoading(prev => ({ ...prev, [extractionId]: true }));
    
    try {
      await axios.post(`${API}/ai-learning/submit-review`, {
        extraction_id: extractionId,
        evaluation_score: score,
        corrected_primary_color: correctedColor
      }, { withCredentials: true });
      
      toast.success(`Review saved: ${score}%`);
      
      // Update local state
      setExtractions(prev => prev.map(ext => 
        ext.extraction_id === extractionId 
          ? { ...ext, reviewed: true, review_score: score, corrected_color: correctedColor }
          : ext
      ));
      
      // Refresh stats
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to submit review:', error);
      toast.error('Failed to save review');
    } finally {
      setReviewLoading(prev => ({ ...prev, [extractionId]: false }));
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="heading-display text-2xl font-medium flex items-center gap-2">
            <Brain className="w-6 h-6 text-purple-500" />
            AI Learning Dashboard
          </h2>
          <p className="text-muted-foreground mt-1">
            Track color recognition accuracy and review results
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={activeView === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveView('overview')}
          >
            <BarChart3 className="w-4 h-4 mr-2" /> Overview
          </Button>
          <Button 
            variant={activeView === 'review' ? 'default' : 'outline'}
            onClick={() => setActiveView('review')}
          >
            <ImageIcon className="w-4 h-4 mr-2" /> Review Images
          </Button>
          <Button variant="outline" onClick={fetchDashboardData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* OVERVIEW VIEW */}
      {activeView === 'overview' && stats && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Total Reviewed */}
            <div className="bg-white border border-border p-6 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Total Reviewed</p>
              <p className="text-3xl font-bold">{stats.totals.reviewed}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.totals.pending} pending
              </p>
            </div>

            {/* Perfect (100%) */}
            <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <p className="text-sm text-green-700">Perfect (100%)</p>
              </div>
              <p className="text-3xl font-bold text-green-700">{stats.scores.perfect.percentage}%</p>
              <p className="text-xs text-green-600">{stats.scores.perfect.count} reviews</p>
            </div>

            {/* Partial (50%) */}
            <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-sr-gold" />
                <p className="text-sm text-amber-700">Partial (50%)</p>
              </div>
              <p className="text-3xl font-bold text-amber-700">{stats.scores.partial.percentage}%</p>
              <p className="text-xs text-amber-600">{stats.scores.partial.count} reviews</p>
            </div>

            {/* Wrong (0%) */}
            <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <XCircle className="w-4 h-4 text-red-500" />
                <p className="text-sm text-red-700">Wrong (0%)</p>
              </div>
              <p className="text-3xl font-bold text-red-700">{stats.scores.wrong.percentage}%</p>
              <p className="text-xs text-red-600">{stats.scores.wrong.count} reviews</p>
            </div>
          </div>

          {/* Accuracy Score */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 p-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700 mb-1">Overall Accuracy Score</p>
                <p className="text-5xl font-bold text-purple-700">{stats.accuracy_score}%</p>
              </div>
              {trend && (
                <div className="text-right">
                  <p className="text-sm text-purple-600 mb-1">30-Day Trend</p>
                  <div className="flex items-center gap-2">
                    {trend.direction === 'improving' && (
                      <>
                        <TrendingUp className="w-6 h-6 text-green-500" />
                        <span className="text-green-600 font-medium">Improving</span>
                      </>
                    )}
                    {trend.direction === 'degrading' && (
                      <>
                        <TrendingDown className="w-6 h-6 text-red-500" />
                        <span className="text-red-600 font-medium">Degrading</span>
                      </>
                    )}
                    {trend.direction === 'stable' && (
                      <>
                        <Minus className="w-6 h-6 text-stone-500" />
                        <span className="text-stone-600 font-medium">Stable</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Color-Specific Accuracy */}
          {colorAccuracy && colorAccuracy.by_color?.length > 0 && (
            <div className="bg-white border border-border rounded-lg p-6">
              <h3 className="font-medium mb-4">Accuracy by Color</h3>
              <div className="space-y-3">
                {colorAccuracy.by_color.map((item) => (
                  <div key={item.color} className="flex items-center gap-4">
                    <div className="w-20 font-medium">{item.color}</div>
                    <div className="flex-1">
                      <div className="h-4 bg-stone-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            item.accuracy >= 80 ? 'bg-green-500' :
                            item.accuracy >= 60 ? 'bg-sr-gold' : 'bg-red-500'
                          }`}
                          style={{ width: `${item.accuracy}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-16 text-right">
                      <span className={`font-medium ${
                        item.accuracy >= 80 ? 'text-green-600' :
                        item.accuracy >= 60 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {item.accuracy}%
                      </span>
                    </div>
                    <div className="w-20 text-right text-sm text-muted-foreground">
                      {item.total} reviews
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Common Errors */}
          {commonErrors && commonErrors.common_errors?.length > 0 && (
            <div className="bg-white border border-border rounded-lg p-6">
              <h3 className="font-medium mb-4 text-red-600">Common Misclassifications</h3>
              <div className="space-y-2">
                {commonErrors.common_errors.map((err, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-3 bg-red-50 rounded">
                    <span className="font-medium text-red-700">{err.should_be}</span>
                    <span className="text-stone-400">→ classified as →</span>
                    <span className="text-red-600">{err.detected_as}</span>
                    <span className="ml-auto text-sm text-red-600">{err.count} times</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* REVIEW VIEW */}
      {activeView === 'review' && (
        <>
          {/* Filters */}
          <div className="flex gap-4 items-center bg-stone-50 p-4 rounded-lg">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={filterColor} onValueChange={setFilterColor}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by color" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Colors</SelectItem>
                {COLOR_OPTIONS.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterReviewed} onValueChange={setFilterReviewed}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Review status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto text-sm text-muted-foreground">
              Showing {offset + 1}-{Math.min(offset + limit, total)} of {total}
            </div>
          </div>

          {/* Image Review Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {extractions.map((ext) => (
              <ImageReviewCard 
                key={ext.extraction_id}
                extraction={ext}
                onReview={submitReview}
                loading={reviewLoading[ext.extraction_id]}
              />
            ))}
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="flex justify-center gap-2">
              <Button 
                variant="outline" 
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </Button>
              <Button 
                variant="outline" 
                disabled={offset + limit >= total}
                onClick={() => setOffset(offset + limit)}
              >
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {extractions.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No images to review with current filters</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Image Review Card Component
const ImageReviewCard = ({ extraction, onReview, loading }) => {
  const [selectedCorrection, setSelectedCorrection] = useState('');
  
  const isReviewed = extraction.reviewed;
  
  return (
    <div className={`bg-white border rounded-lg overflow-hidden ${
      isReviewed ? 'border-green-200' : 'border-border'
    }`}>
      {/* Image */}
      <div className="aspect-square bg-stone-100 relative">
        {extraction.image_url ? (
          <img 
            src={extraction.image_url} 
            alt="Uploaded material"
            className="w-full h-full object-cover"
            onError={(e) => e.target.src = '/placeholder-image.png'}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-12 h-12 text-stone-300" />
          </div>
        )}
        
        {/* Review Status Badge */}
        {isReviewed && (
          <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
            extraction.review_score === 100 ? 'bg-green-500 text-white' :
            extraction.review_score === 50 ? 'bg-sr-gold text-white' :
            'bg-red-500 text-white'
          }`}>
            {extraction.review_score}%
          </div>
        )}
      </div>
      
      {/* Detection Results */}
      <div className="p-4 space-y-3">
        <div>
          <p className="text-xs text-muted-foreground">Detected Primary</p>
          <p className="font-medium text-lg">{extraction.primary_color}</p>
        </div>
        
        {extraction.secondary_colors?.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground">Secondary</p>
            <p className="text-sm">{extraction.secondary_colors.join(', ')}</p>
          </div>
        )}
        
        {extraction.weights && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">Weights</p>
            <div className="text-xs space-y-1">
              {Object.entries(extraction.weights).slice(0, 3).map(([color, weight]) => (
                <div key={color} className="flex items-center gap-2">
                  <span className="w-12">{color}</span>
                  <div className="flex-1 h-2 bg-stone-100 rounded overflow-hidden">
                    <div 
                      className="h-full bg-primary"
                      style={{ width: `${weight * 100}%` }}
                    />
                  </div>
                  <span className="w-10 text-right">{(weight * 100).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Review Buttons */}
        {!isReviewed && (
          <>
            <div className="flex gap-2 pt-2">
              <Button 
                size="sm" 
                className="flex-1 bg-red-500 hover:bg-red-600"
                onClick={() => onReview(extraction.extraction_id, 0, selectedCorrection || null)}
                disabled={loading}
              >
                0%
              </Button>
              <Button 
                size="sm" 
                className="flex-1 bg-sr-gold hover:bg-sr-gold-hover"
                onClick={() => onReview(extraction.extraction_id, 50, selectedCorrection || null)}
                disabled={loading}
              >
                50%
              </Button>
              <Button 
                size="sm" 
                className="flex-1 bg-green-500 hover:bg-green-600"
                onClick={() => onReview(extraction.extraction_id, 100)}
                disabled={loading}
              >
                100%
              </Button>
            </div>
            
            {/* Correction Dropdown */}
            <Select value={selectedCorrection} onValueChange={setSelectedCorrection}>
              <SelectTrigger className="w-full text-sm">
                <SelectValue placeholder="Correct to..." />
              </SelectTrigger>
              <SelectContent>
                {COLOR_OPTIONS.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        
        {/* Show correction if reviewed */}
        {isReviewed && extraction.corrected_color && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">Corrected to</p>
            <p className="font-medium text-green-600">{extraction.corrected_color}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AILearningDashboard;
